import {
  fetchFieldCustomizations,
  fetchInstanceGeneralSettings,
  putFieldCustomizations,
  putInstanceGeneralSettings,
  type FieldCustomizationFieldDto,
} from "@/api/user-settings";
import { useRefetchFieldCustomizations } from "@/context/FieldCustomizationOverrideContext";
import { toast } from "@/hooks/use-toast";
import {
  getMainLanguage,
  reloadAppTranslationResources,
  toSupportedLocale,
  type SupportedLocale,
} from "@/i18n";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const SUPPORTED_LOCALES: SupportedLocale[] = ["fr", "en", "es", "nl"];

function cloneDraftFromFields(
  fields: { key: string; customLabels: Record<string, string> }[],
): Record<string, Record<SupportedLocale, string>> {
  const out: Record<string, Record<SupportedLocale, string>> = {};
  for (const f of fields) {
    out[f.key] = {
      fr: f.customLabels.fr ?? "",
      en: f.customLabels.en ?? "",
      es: f.customLabels.es ?? "",
      nl: f.customLabels.nl ?? "",
    };
  }
  return out;
}

export const SAVE_PHASES = [1, 2, 3] as const;
export type SavePhase = (typeof SAVE_PHASES)[number];

type Snapshot = {
  draft: Record<string, Record<SupportedLocale, string>>;
  provenanceMode: boolean;
  fieldEditLocales: Record<string, SupportedLocale>;
};

export function useGeneralSettingsFormState() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refetchOverrideCtx = useRefetchFieldCustomizations();

  const { data: fieldCustomizations, isLoading: fcLoading } = useQuery({
    queryKey: ["settings", "field-customizations", "form"],
    queryFn: () => fetchFieldCustomizations(),
  });
  const { data: generalSettings, isLoading: generalLoading } = useQuery({
    queryKey: ["settings", "general"],
    queryFn: fetchInstanceGeneralSettings,
  });

  const [fieldEditLocales, setFieldEditLocales] = useState<
    Record<string, SupportedLocale>
  >({});
  const [draft, setDraft] = useState<
    Record<string, Record<SupportedLocale, string>>
  >({});
  const [provenanceMode, setProvenanceMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savePhase, setSavePhase] = useState<SavePhase>(1);
  const snapshot = useRef<Snapshot | null>(null);

  useEffect(() => {
    if (!fieldCustomizations?.fields) return;
    const d = cloneDraftFromFields(fieldCustomizations.fields);
    const prov = generalSettings?.provenanceMode ?? false;
    setDraft(d);
    setProvenanceMode(prov);
    setFieldEditLocales((prev) => {
      const defaultLocale = toSupportedLocale(getMainLanguage());
      const next: Record<string, SupportedLocale> = {};
      for (const f of fieldCustomizations.fields) {
        next[f.key] = prev[f.key] ?? defaultLocale;
      }
      snapshot.current = {
        draft: JSON.parse(JSON.stringify(d)) as typeof d,
        provenanceMode: prov,
        fieldEditLocales: JSON.parse(JSON.stringify(next)),
      };
      return next;
    });
  }, [fieldCustomizations, generalSettings]);

  const loading = fcLoading || generalLoading;

  const reset = useCallback(() => {
    if (!snapshot.current) return;
    setDraft(JSON.parse(JSON.stringify(snapshot.current.draft)));
    setProvenanceMode(snapshot.current.provenanceMode);
    setFieldEditLocales(
      JSON.parse(JSON.stringify(snapshot.current.fieldEditLocales)),
    );
  }, []);

  const save = useCallback(async () => {
    if (!fieldCustomizations?.fields || isSaving) return;
    setIsSaving(true);
    setSavePhase(1);
    try {
      await putFieldCustomizations({
        fields: fieldCustomizations.fields.map((f) => ({
          key: f.key,
          customLabels: draft[f.key] ?? {
            fr: "",
            en: "",
            es: "",
            nl: "",
          },
        })),
      });
      await putInstanceGeneralSettings({ provenanceMode });
      snapshot.current = {
        draft: JSON.parse(JSON.stringify(draft)),
        provenanceMode,
        fieldEditLocales: JSON.parse(JSON.stringify(fieldEditLocales)),
      };
      setSavePhase(2);
      await qc.invalidateQueries({
        queryKey: ["settings", "field-customizations", "form"],
      });
      await qc.invalidateQueries({ queryKey: ["settings", "general"] });
      await refetchOverrideCtx();
      setSavePhase(3);
      await reloadAppTranslationResources();
      toast({
        title: t("settingsSavedToastTitle"),
        description: t("settingsSavedToastDescription"),
        duration: 3000,
        variant: "success",
      });
    } catch {
      toast({
        title: t("settingsErrorToastTitle"),
        description: t("settingsErrorToastDescription"),
        duration: 3000,
        variant: "destructive",
      });
    } finally {
      setSavePhase(1);
      setIsSaving(false);
    }
  }, [
    draft,
    fieldCustomizations?.fields,
    fieldEditLocales,
    isSaving,
    provenanceMode,
    qc,
    refetchOverrideCtx,
    t,
  ]);

  return {
    fields: (fieldCustomizations?.fields ?? []) as FieldCustomizationFieldDto[],
    draft,
    setDraft,
    fieldEditLocales,
    setFieldEditLocales,
    provenanceMode,
    setProvenanceMode,
    isSaving,
    savePhase,
    loading,
    reset,
    save,
    hasFields: Boolean(fieldCustomizations?.fields?.length),
    loadingInitial: loading && !fieldCustomizations?.fields,
    supportedLocales: SUPPORTED_LOCALES,
  };
}
